import * as THREE from 'three';
import { WallType, WallConnectionType, WallConnectionStart, WallConnectionEnd, WallShapeType } from '../types/floorplannerTypes';
import { WallConnectionTopology } from '../types/wallCacheTypes';

export class WallChangeDetector {
  /**
   * Enhanced shape change detection that includes connection topology
   */
  static shapeChanged(shape: WallShapeType, wall: WallType, topology?: WallConnectionTopology): boolean {
    // Basic position/width checks
    if (shape.wall.start.x !== wall.start.x || shape.wall.start.y !== wall.start.y) {
      return true;
    }
    if (shape.wall.end.x !== wall.end.x || shape.wall.end.y !== wall.end.y) {
      return true;
    }
    if (shape.wall.wallWidth !== wall.wallWidth) {
      return true;
    }

    // Check connection topology if provided
    if (topology) {
      const currentConnectionHash = this.generateConnectionHash(wall);
      if (currentConnectionHash !== topology.connectionHash) {
        return true;
      }
    }

    return false;
  }

  /**
   * Generate a hash of wall's connection topology
   */
  static generateConnectionHash(wall: WallType): string {
    if (!wall.centerConnections || wall.centerConnections.length === 0) {
      return 'no-connections';
    }

    const sortedConnections = [...wall.centerConnections]
      .sort((a, b) => a.sourcePosition - b.sourcePosition)
      .map(c => `${c.id}:${c.sourcePosition}:${c.targetPosition}`)
      .join('|');

    return this.simpleHash(sortedConnections);
  }

  /**
   * Generate a position hash for the wall
   */
  static generatePositionHash(wall: WallType): string {
    const precision = 3; // 3 decimal places for position precision
    const startKey = `${wall.start.x.toFixed(precision)},${wall.start.y.toFixed(precision)}`;
    const endKey = `${wall.end.x.toFixed(precision)},${wall.end.y.toFixed(precision)}`;
    const widthKey = (wall.wallWidth || 0).toFixed(precision);

    return this.simpleHash(`${startKey}|${endKey}|${widthKey}`);
  }

  /**
   * Generate complete wall state hash including position and connections
   */
  static generateWallStateHash(wall: WallType): string {
    const positionHash = this.generatePositionHash(wall);
    const connectionHash = this.generateConnectionHash(wall);
    return `${positionHash}|${connectionHash}`;
  }

  /**
   * Create connection topology for a wall
   */
  static createConnectionTopology(wall: WallType): WallConnectionTopology {
    return {
      wallId: wall.id,
      connections: wall.centerConnections || [],
      positionHash: this.generatePositionHash(wall),
      connectionHash: this.generateConnectionHash(wall)
    };
  }

  /**
   * Check if two connection topologies are equivalent
   */
  static topologyChanged(topology1: WallConnectionTopology, topology2: WallConnectionTopology): boolean {
    return topology1.connectionHash !== topology2.connectionHash ||
           topology1.positionHash !== topology2.positionHash;
  }

  /**
   * Simple hash function for strings
   */
  private static simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Check if wall position actually changed significantly
   */
  static positionChangedSignificantly(
    oldPos: THREE.Vector2,
    newPos: THREE.Vector2,
    threshold: number = 0.001
  ): boolean {
    return oldPos.distanceTo(newPos) > threshold;
  }

  /**
   * Get all walls that might be affected by a wall change
   */
  static getAffectedWalls(wallId: string, allWalls: Map<string, WallType>): string[] {
    const wall = allWalls.get(wallId);
    if (!wall) return [];

    const affected = new Set<string>();
    affected.add(wallId);

    // Add directly connected walls
    wall.centerConnections?.forEach(connection => {
      affected.add(connection.id);
    });

    // Add walls that connect to this wall
    allWalls.forEach(otherWall => {
      if (otherWall.id !== wallId) {
        otherWall.centerConnections?.forEach(connection => {
          if (connection.id === wallId) {
            affected.add(otherWall.id);
          }
        });
      }
    });

    return Array.from(affected);
  }
}